import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, problemMessage } from "@/api/client";
import type { AnimalStatus, ChangeAnimalStatusRequest, Guid } from "@/api/types";

/** The seven codes, in the order the owner reads them (ef-059: the last three are new). */
export const STATUS_OPTIONS: { value: AnimalStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "sold", label: "Sold" },
  { value: "deceased", label: "Deceased" },
  { value: "culled", label: "Culled" },
  { value: "butchered", label: "Butchered" },
  { value: "transferred", label: "Transferred" },
  { value: "lost", label: "Lost" },
];

/** Today as a local `YYYY-MM-DD` — what a date input holds and what the API takes. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * ef-059-7 — the "Change status" form that lives inside a roster row's `RowActions` panel:
 * a status code, the day it took effect (defaults to today, never in the future) and an
 * optional reason. Extracted from `Livestock.tsx` so that page's CSS budget stays about the
 * roster; it deliberately reuses the page's `livestock-page__*` form classes rather than
 * forking a second copy of them.
 *
 * Owns its own field state: `RowActions` unmounts its children on close, so a half-filled form
 * resets itself without the row having to clear anything. The server's validation sentence
 * (before the birth date, before the latest change) is shown verbatim in a `role="alert"` and
 * the date field is marked `aria-invalid` — what the server refused is read off the DOM.
 */
export function AnimalStatusForm({
  animalId,
  currentStatus,
  onSubmit,
  onBack,
  onDone,
}: {
  animalId: Guid;
  currentStatus: AnimalStatus;
  onSubmit: (id: Guid, body: ChangeAnimalStatusRequest) => Promise<void>;
  onBack: () => void;
  onDone: () => void;
}) {
  const [toStatus, setToStatus] = useState<AnimalStatus>(currentStatus);
  const [effectiveOn, setEffectiveOn] = useState(todayIso);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dateInvalid, setDateInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const statusId = useId();
  const statusRef = useRef<HTMLSelectElement>(null);
  const dateId = useId();
  const reasonId = useId();
  const errorId = useId();

  // The panel opened because the owner chose "Change status" — that button is now unmounted,
  // so focus would fall to <body>. Land on the first field instead.
  useEffect(() => {
    statusRef.current?.focus();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setDateInvalid(false);
    setSubmitting(true);
    try {
      await onSubmit(animalId, {
        toStatus,
        reason: reason.trim() === "" ? undefined : reason.trim(),
        effectiveOn: effectiveOn === "" ? undefined : effectiveOn,
      });
      onDone();
    } catch (err) {
      // A 409 means this animal is already in that status — refused, not silently re-logged.
      // Read off ApiError.status so the copy names the status back ("Already active.").
      if (err instanceof ApiError && err.status === 409) {
        setError(`Already ${toStatus}.`);
      } else {
        // A 400 here is a validation problem on the date (birth date / latest change / future).
        if (err instanceof ApiError && err.status === 400) setDateInvalid(true);
        setError(problemMessage(err, "Could not change status."));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className="livestock-page__status-form"
      aria-busy={submitting}
      data-state={error ? "error" : submitting ? "loading" : "ready"}
      onSubmit={(e) => void submit(e)}
    >
      <span className="livestock-page__field">
        <label htmlFor={statusId}>Status</label>
        <select id={statusId} ref={statusRef} value={toStatus} onChange={(e) => setToStatus(e.target.value as AnimalStatus)}>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </span>
      <span className="livestock-page__field">
        <label htmlFor={dateId}>Effective date</label>
        <input
          id={dateId}
          type="date"
          max={todayIso()}
          value={effectiveOn}
          aria-invalid={dateInvalid || undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => setEffectiveOn(e.target.value)}
        />
      </span>
      <span className="livestock-page__field">
        <label htmlFor={reasonId}>Reason (optional)</label>
        <input id={reasonId} type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      </span>
      {error && (
        <p id={errorId} className="livestock-page__field-error" role="alert">
          {error}
        </p>
      )}
      <span className="livestock-page__status-actions">
        <button type="button" className="btn btn--ghost livestock-page__btn" disabled={submitting} onClick={onBack}>
          Back
        </button>
        <button type="submit" className="btn btn--primary livestock-page__btn" disabled={submitting}>
          {submitting ? "…" : "Change status"}
        </button>
      </span>
    </form>
  );
}
